import { Link } from "react-router-dom";
import {
    Facebook,
    Instagram,
    Linkedin,
    Twitter,
    Mail,
    Phone,
    MapPin,
    ArrowRight,
    Calculator,
    MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";

const Footer = () => {
    const currentYear = new Date().getFullYear();

    return (
        <footer className="hidden lg:block bg-white text-slate-600 border-t border-slate-200">
            <div className="max-w-7xl mx-auto px-4 py-12 lg:py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                    {/* Company Info */}
                    <div className="space-y-6 lg:pr-8">
                        <Link to="/" className="flex items-center gap-3 group">
                            <img src="/logo_hd_small.webp" width="160" height="60" loading="lazy" alt="Make Me Filer Logo" className="h-[3.75rem] w-auto object-contain transition-transform group-hover:scale-105" />
                            <span className="text-2xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-700">
                                Make Me Filer
                            </span>
                        </Link>
                        <p className="text-slate-500 leading-relaxed">
                            Simplifying tax filing for everyone. We provide professional tax services,
                            NTN registration, and financial consultancy with ease and accuracy.
                        </p>
                        <div className="flex space-x-4">
                            {[
                                { icon: Facebook, href: "#", name: "Facebook" },
                                { icon: Twitter, href: "#", name: "Twitter" },
                                { icon: Instagram, href: "#", name: "Instagram" },
                                { icon: Linkedin, href: "#", name: "LinkedIn" },
                            ].map((social, index) => (
                                <a
                                    key={index}
                                    href={social.href}
                                    aria-label={`Visit our ${social.name} page`}
                                    className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200 hover:scale-110"
                                >
                                    <social.icon className="h-5 w-5" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-slate-900 font-bold text-lg mb-6">Quick Links</h3>
                        <ul className="space-y-4">
                            {[
                                { name: "Home", path: "/" },
                                { name: "About Us", path: "/about" },
                                { name: "Services", path: "/services" },
                                { name: "Blog", path: "/blog" },
                                { name: "Pricing", path: "/pricing" },
                                { name: "System Status", path: "/status" },
                                { name: "Contact", path: "/contact" },
                            ].map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.path}
                                        className="text-slate-600 hover:text-blue-600 transition-colors flex items-center group"
                                    >
                                        <ArrowRight className="h-4 w-4 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Services - Links Removed */}
                    <div>
                        <h3 className="text-slate-900 font-bold text-lg mb-6">Our Services</h3>
                        <ul className="space-y-4">
                            {[
                                "Income Tax Filing",
                                "NTN Registration",
                                "Sales Tax Registration",
                                "Company Registration",
                                "Tax Consultancy",
                            ].map((service) => (
                                <li key={service} className="text-slate-600 flex items-center">
                                    <ArrowRight className="h-4 w-4 mr-2 text-slate-400" />
                                    {service}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-6">
                        <h3 className="text-slate-900 font-bold text-lg mb-6">Contact Us</h3>
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <MapPin className="h-5 w-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                                <span className="text-slate-600">
                                    House No 4C/1, Sector E-2, Mirpur Azad Kashmir
                                </span>
                            </li>
                            <li className="flex items-center">
                                <Phone className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
                                <span className="text-slate-600">03225511770</span>
                            </li>
                            <li className="flex items-center">
                                <Mail className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
                                <span className="text-slate-600">info@makemefiler.com</span>
                            </li>
                        </ul>
                        <Button
                            className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-slate-950 font-bold shadow-lg shadow-green-500/20"
                            asChild
                        >
                            <a
                                href="https://wa.me/923225511770"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2"
                                aria-label="Contact us on WhatsApp"
                            >
                                <MessageCircle className="h-5 w-5" />
                                WhatsApp Us
                            </a>
                        </Button>
                    </div>
                </div>

                <div className="mt-8 lg:mt-12">
                    {/* Blue Horizontal Divider */}
                    <div className="h-px w-full bg-blue-100" />
                    <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
                        <p className="text-slate-500 text-sm">
                            © {currentYear} Make Me Filer. All rights reserved.
                        </p>
                        <div className="flex space-x-6 text-sm text-slate-500">
                            <Link to="/privacy" className="hover:text-blue-600 transition-colors">
                                Privacy Policy
                            </Link>
                            <Link to="/terms" className="hover:text-blue-600 transition-colors">
                                Terms of Service
                            </Link>
                            <Link to="/cookies" className="hover:text-blue-600 transition-colors">
                                Cookie Policy
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
